import { useGetQuestionnaire, useSubmitQuestionnaire, useUpdateIndividualQuestion } from '@/modules/vendors/requests';
import { Question, QuestionnaireFormData } from '@/modules/vendors/types';
import { QueryKeys } from '@/shared/constants/QueryKeys';
import { usePagination } from '@/shared/hooks/usePagination';
import { addNotification } from '@/shared/states/notification';
import { UploadedFile } from '@/shared/types/file-upload';
import { Spinner } from '@nextui-org/react';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AutoFillWithAIModal from './AutoFillWithAIModal';
import QuestionField from './QuestionField';
import QuestionnaireHeader from './QuestionnaireHeader';
import RespondOfflineModal from './RespondOfflineModal';
import { TablePagination } from './TablePagination';

// Types for optimized question visibility management
interface ChildrenMapEntry {
  type: 'free_text' | 'single_choice' | 'multiple_choice';
  children: Record<string, string[]>; // suffix -> array of child question numbers
}

interface PreprocessedQuestions {
  allQuestions: Map<string, Question>; // question_number -> Question
  childrenMap: Map<string, ChildrenMapEntry>; // parent question_number -> children info
  topLevelQuestions: string[]; // question numbers without dots
}

/**
 * Preprocesses questions to build efficient lookup structures
 * This runs once when questions are loaded, not on every render
 */
function preprocessQuestions(questions: Question[]): PreprocessedQuestions {
  const allQuestions = new Map<string, Question>();
  const childrenMap = new Map<string, ChildrenMapEntry>();
  const topLevelQuestions: string[] = [];

  // First pass: Build question lookup map and identify top-level questions
  for (const question of questions) {
    const questionNumber = question.question_number.toString();
    allQuestions.set(questionNumber, question);

    if (!questionNumber.includes('.')) {
      topLevelQuestions.push(questionNumber);
    }
  }

  // Second pass: Build children map
  for (const question of questions) {
    const questionNumber = question.question_number.toString();

    // Skip top-level questions (they are not children)
    if (!questionNumber.includes('.')) {
      continue;
    }

    // Parse parent and child suffix
    const parts = questionNumber.split('.');
    const parentQuestionNumber = parts.slice(0, -1).join('.');
    const childSuffix = parts[parts.length - 1];

    // Get parent question
    const parentQuestion = allQuestions.get(parentQuestionNumber);
    if (!parentQuestion) {
      continue; // Parent not found, skip
    }

    // Initialize parent's entry in childrenMap if not exists
    if (!childrenMap.has(parentQuestionNumber)) {
      childrenMap.set(parentQuestionNumber, {
        type: parentQuestion.question_type,
        children: {}
      });
    }

    // Add this child to the parent's children map
    const parentEntry = childrenMap.get(parentQuestionNumber)!;
    if (!parentEntry.children[childSuffix]) {
      parentEntry.children[childSuffix] = [];
    }
    parentEntry.children[childSuffix].push(questionNumber);
  }

  return {
    allQuestions,
    childrenMap,
    topLevelQuestions
  };
}

interface QuestionsFormProps {
  questionnaireUrl: string | null | undefined;
  token: string;
  onSavingStateChange?: (isSaving: boolean) => void;
  onPaginationChange?: (paginationComponent: React.ReactNode) => void;
  questionnaireExcelUrl?: string;
  onProgressChange?: (totalRequired: number, completedRequired: number) => void;
  onFormRemount?: () => void;
}

const QuestionsForm: React.FC<QuestionsFormProps> = ({
  questionnaireUrl,
  token,
  onSavingStateChange,
  onPaginationChange,
  questionnaireExcelUrl,
  onProgressChange,
  onFormRemount
}) => {
  const [formData, setFormData] = useState<QuestionnaireFormData>({});
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [changedQuestions, setChangedQuestions] = useState<Set<number>>(new Set());

  // Use ref to track formData changes for stable dependencies
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  // Header state
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activelyEditingQuestions, setActivelyEditingQuestions] = useState<Set<number>>(new Set());

  // Modal states
  const [isAutoFillModalOpen, setIsAutoFillModalOpen] = useState(false);
  const [isRespondOfflineModalOpen, setIsRespondOfflineModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  // Pagination
  const { paginationState, setPaginationState, resetPageNumber } = usePagination(1, 25);
  const { currentPage, rowsPerPage } = paginationState;
  const { handlePageChange, handleRowsPerPageChange } = setPaginationState;

  // Fetch questionnaire data
  const {
    data: questions = [],
    isLoading: isLoadingQuestions,
    error: questionError,
    isError: hasQuestionError
  } = useGetQuestionnaire(questionnaireUrl);

  const queryClient = useQueryClient();

  // Preprocess questions once when loaded - O(n) operation, runs only once
  const preprocessedData = useMemo(() => {
    if (!questions.length) {
      return {
        allQuestions: new Map<string, Question>(),
        childrenMap: new Map<string, ChildrenMapEntry>(),
        topLevelQuestions: []
      };
    }
    return preprocessQuestions(questions);
  }, [questions]);

  /**
   * Get children that should be visible based on parent's response
   * This is O(1) lookup using preprocessed data
   */
  const getVisibleChildren = useCallback((parentQuestionNumber: string, parentResponse: string | string[]): string[] => {
    const childrenEntry = preprocessedData.childrenMap.get(parentQuestionNumber);
    if (!childrenEntry) {
      return []; // No children for this parent
    }

    const children: string[] = [];

    // For free_text parents, show ".a" children if parent has any response
    if (childrenEntry.type === 'free_text') {
      if (parentResponse && (typeof parentResponse === 'string' && parentResponse.trim() !== '')) {
        const aChildren = childrenEntry.children['a'] || [];
        children.push(...aChildren);
      }
      return children;
    }

    // For single_choice parents
    if (childrenEntry.type === 'single_choice' && typeof parentResponse === 'string') {
      const parentQuestion = preprocessedData.allQuestions.get(parentQuestionNumber);
      if (!parentQuestion || !parentQuestion.response_choices) {
        return children;
      }

      // Find the index of the selected response
      const selectedIndex = parentQuestion.response_choices.indexOf(parentResponse);
      if (selectedIndex === -1) {
        return children;
      }

      // Convert index to suffix (0 -> 'a', 1 -> 'b', etc.)
      const suffix = String.fromCharCode('a'.charCodeAt(0) + selectedIndex);
      const suffixChildren = childrenEntry.children[suffix] || [];
      children.push(...suffixChildren);
    }

    // For multiple_choice parents
    if (childrenEntry.type === 'multiple_choice' && Array.isArray(parentResponse)) {
      const parentQuestion = preprocessedData.allQuestions.get(parentQuestionNumber);
      if (!parentQuestion || !parentQuestion.response_choices) {
        return children;
      }

      // For each selected response, add its children
      for (const selectedResponse of parentResponse) {
        const selectedIndex = parentQuestion.response_choices.indexOf(selectedResponse);
        if (selectedIndex !== -1) {
          const suffix = String.fromCharCode('a'.charCodeAt(0) + selectedIndex);
          const suffixChildren = childrenEntry.children[suffix] || [];
          children.push(...suffixChildren);
        }
      }
    }

    return children;
  }, [preprocessedData]);

  /**
   * Recursively build visible questions list
   * This traverses the tree depth-first to maintain proper order
   */
  const buildVisibleQuestionsList = useCallback((questionNumber: string, result: string[] = []): void => {
    result.push(questionNumber);

    // Get the question object to find its actual question_number (which is a number)
    const question = preprocessedData.allQuestions.get(questionNumber);
    if (!question) {
      return;
    }

    // Get response for this question using the numeric question_number
    const response = formData[question.question_number];

    // If this question has a response, check if it has children to show
    if (response !== undefined && response !== null && response !== '') {
      const children = getVisibleChildren(questionNumber, response);

      // Recursively add visible children
      for (const childQuestionNumber of children) {
        buildVisibleQuestionsList(childQuestionNumber, result);
      }
    }
  }, [formData, getVisibleChildren, preprocessedData.allQuestions]);

  /**
   * Compute the current list of visible questions based on formData
   * Only runs when formData changes, not on every render
   */
  const visibleQuestions = useMemo(() => {
    const visible: string[] = [];

    // Start with top-level questions
    for (const topLevelQuestion of preprocessedData.topLevelQuestions) {
      buildVisibleQuestionsList(topLevelQuestion, visible);
    }

    return visible;
  }, [preprocessedData.topLevelQuestions, buildVisibleQuestionsList]);

  // Get actual Question objects from visible question numbers
  const visibleQuestionObjects = useMemo(() => {
    return visibleQuestions
      .map(qNum => preprocessedData.allQuestions.get(qNum))
      .filter((q): q is Question => q !== undefined);
  }, [visibleQuestions, preprocessedData.allQuestions]);

  // Filter questions based on search and status
  const filteredQuestions = useMemo(() => {
    let filtered = visibleQuestionObjects;

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(question =>
        question.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        question.question_number.toString().includes(searchQuery)
      );
    }

    // Filter by status (all/pending/completed/mandatory)
    if (statusFilter === 'mandatory') {
      filtered = filtered.filter(question => question.required);
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter(question => {
        const questionNumber = question.question_number;
        const value = formData[questionNumber];

        // If question is actively being edited, keep it visible in pending
        if (activelyEditingQuestions.has(questionNumber)) {
          return true;
        }

        // Otherwise, check if it's truly unanswered
        if (question.question_type === 'multiple_choice') {
          return !Array.isArray(value) || value.length === 0;
        } else {
          return !value || (typeof value === 'string' && value.trim() === '');
        }
      });
    } else if (statusFilter === 'completed') {
      filtered = filtered.filter(question => {
        const questionNumber = question.question_number;
        const value = formData[questionNumber];
        if (question.question_type === 'multiple_choice') {
          return Array.isArray(value) && value.length > 0;
        } else {
          return value && (typeof value === 'string' && value.trim() !== '');
        }
      });
    }
    // For 'all', no status filtering is applied

    return filtered;
  }, [visibleQuestionObjects, searchQuery, statusFilter, formData, activelyEditingQuestions]);
  const totalQuestions = filteredQuestions.length;
  const totalPages = Math.ceil(totalQuestions / rowsPerPage);
  
  const paginatedQuestions = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredQuestions.slice(startIndex, endIndex);
  }, [filteredQuestions, currentPage, rowsPerPage]);

  // Submit questionnaire mutation
  const { mutate: submitQuestionnaire, isPending: isSubmitting } = useSubmitQuestionnaire();

  // Update individual question mutation
  const { mutate: updateQuestion, isPending: isUpdatingQuestion } = useUpdateIndividualQuestion();

  // Initialize form data when questions are loaded
  // Use a ref to track if we've already initialized to prevent overwriting user changes
  const hasInitialized = useRef(false);
  const shouldReinitialize = useRef(false);

  useEffect(() => {
    if (questions.length > 0 && (!hasInitialized.current || shouldReinitialize.current)) {
      const initialData: QuestionnaireFormData = {};
      questions.forEach((question) => {
        const questionNumber = question.question_number;
        // Initialize with existing response value or empty based on type
        if (question.question_type === 'multiple_choice') {
          initialData[questionNumber] = Array.isArray(question.response_value)
            ? question.response_value
            : [];
        } else {
          initialData[questionNumber] = typeof question.response_value === 'string'
            ? question.response_value
            : '';
        }
      });
      setFormData(initialData);
      hasInitialized.current = true;
      shouldReinitialize.current = false;
    }
  }, [questions]);

  // Calculate and report progress when questions or form data changes
  // Only count VISIBLE required questions, not hidden sub-questions
  useEffect(() => {
    if (onProgressChange && visibleQuestionObjects && visibleQuestionObjects.length > 0) {
      // Count required questions that are currently visible
      const requiredQuestions = visibleQuestionObjects.filter(question => question.required);
      const totalRequired = requiredQuestions.length;

      // Count completed required questions
      const completedRequired = requiredQuestions.filter(question => {
        const response = formData[question.question_number];

        // Check if question is completed based on its type
        if (question.question_type === 'multiple_choice') {
          return Array.isArray(response) && response.length > 0;
        } else {
          // For 'free_text' and 'single_choice'
          return typeof response === 'string' && response.trim().length > 0;
        }
      }).length;

      // Report progress to parent
      onProgressChange(totalRequired, completedRequired);
    }
  }, [visibleQuestionObjects, formData, onProgressChange]);

  // Handle form field changes
  const handleFieldChange = useCallback((questionNumber: number, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [questionNumber]: value
    }));

    // Mark question as actively being edited
    setActivelyEditingQuestions(prev => new Set(prev).add(questionNumber));

    // Clear any existing error for this field
    if (errors[questionNumber]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionNumber];
        return newErrors;
      });
    }

    // Track changed questions
    setChangedQuestions(prev => new Set(prev).add(questionNumber));
    setHasUnsavedChanges(true);
  }, [errors]);

  // Handle comment changes
  const handleCommentChange = useCallback((questionNumber: number, comment: string) => {
    // Update the question's comments in the questions array directly via query client
    const updatedQuestions = questions.map(q =>
      q.question_number === questionNumber
        ? { ...q, comments: comment }
        : q
    );

    // Update the cache
    queryClient.setQueryData([QueryKeys.Questionnaire, questionnaireUrl], updatedQuestions);

    // Mark question as actively being edited
    setActivelyEditingQuestions(prev => new Set(prev).add(questionNumber));

    // Track changed questions
    setChangedQuestions(prev => new Set(prev).add(questionNumber));
    setHasUnsavedChanges(true);
  }, [questions, queryClient, questionnaireUrl]);

  // Handle immediate checkbox changes - call API directly
  const handleImmediateFieldChange = useCallback((questionNumber: number, value: string | string[]) => {
    // Update local state immediately
    setFormData(prev => ({
      ...prev,
      [questionNumber]: value
    }));

    // Mark question as actively being edited
    setActivelyEditingQuestions(prev => new Set(prev).add(questionNumber));

    // Clear any existing error for this field
    if (errors[questionNumber]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionNumber];
        return newErrors;
      });
    }

    // Call API immediately for checkboxes
    const question = questions.find(q => q.question_number === questionNumber);
    if (question) {
      onSavingStateChange?.(true);

      const updatedQuestion = {
        ...question,
        response_value: value
      };

      updateQuestion(
        {
          questionId: question.id,
          token,
          questionData: updatedQuestion
        },
        {
          onSuccess: () => {
            // For immediate updates, we don't track in changedQuestions
            // since the change is already saved
          },
          onError: (error) => {

            addNotification({
              message: `Failed to save answer for question ${questionNumber}. Please try again.`,
              type: 'error'
            });
          },
          onSettled: () => {
            // Keep saving state true for minimum 2 seconds
            setTimeout(() => {
              onSavingStateChange?.(false);
            }, 2000);
          }
        }
      );
    }
  }, [errors, questions, token, updateQuestion, onSavingStateChange]);  // Clear actively editing status after period of inactivity
  useEffect(() => {
    if (activelyEditingQuestions.size === 0) return;

    const clearEditingTimer = setTimeout(() => {
      setActivelyEditingQuestions(new Set());
    }, 3000); // Clear after 3 seconds of no activity

    return () => clearTimeout(clearEditingTimer);
  }, [activelyEditingQuestions]);

  // Auto-save functionality (debounced)
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const autoSaveTimer = setTimeout(() => {
      handleAutoSave();
    }, 500); // Auto-save after 0.5 seconds of inactivity

    return () => clearTimeout(autoSaveTimer);
  }, [hasUnsavedChanges, changedQuestions.size]); // Include changedQuestions.size to re-trigger when new questions are added

  const handleAutoSave = useCallback(() => {
    if (!hasUnsavedChanges || changedQuestions.size === 0) return;

    onSavingStateChange?.(true);

    // Track successfully updated questions
    const successfullyUpdated = new Set<number>();
    const initialChangedCount = changedQuestions.size;

    // Update all changed questions
    const updatePromises = Array.from(changedQuestions).map(questionNumber => {
      const question = questions.find(q => q.question_number === questionNumber);
      if (!question) return Promise.resolve();

      const updatedQuestion = {
        ...question,
        response_value: formDataRef.current[questionNumber] || (question.question_type === 'multiple_choice' ? [] : '')
      };

      return updateQuestion(
        {
          questionId: question.id,
          token,
          questionData: updatedQuestion
        },
        {
          onSuccess: () => {
            // Mark as successfully updated
            successfullyUpdated.add(questionNumber);
            // Remove from changed questions on success
            setChangedQuestions(prev => {
              const newSet = new Set(prev);
              newSet.delete(questionNumber);
              return newSet;
            });
            // Remove from actively editing questions on success
            setActivelyEditingQuestions(prev => {
              const newSet = new Set(prev);
              newSet.delete(questionNumber);
              return newSet;
            });
          },
          onError: (error) => {

            // Keep the question in changed set for retry
          }
        }
      );
    });

    // Wait for all updates to complete
    Promise.allSettled(updatePromises).then(() => {
      // Clear unsaved changes only if all initially changed questions were successfully updated
      if (successfullyUpdated.size === initialChangedCount) {
        setHasUnsavedChanges(false);
      }
      // Keep saving state true for minimum 2 seconds
      setTimeout(() => {
        onSavingStateChange?.(false);
      }, 2000);
    });
  }, [hasUnsavedChanges, changedQuestions, questions, token, updateQuestion, onSavingStateChange]);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<number, string> = {};
    let isValid = true;

    questions.forEach((question) => {
      const questionNumber = question.question_number;
      const value = formDataRef.current[questionNumber];
      
      // Check if response is empty and question is required
      if (question.required) {
        if (question.question_type === 'multiple_choice') {
          if (!Array.isArray(value) || value.length === 0) {
            newErrors[questionNumber] = 'Please select at least one option.';
            isValid = false;
          }
        } else {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors[questionNumber] = 'This field is required.';
            isValid = false;
          }
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [questions]);

  // Handle form submission
  const handleSubmit = useCallback(() => {
    if (!validateForm()) {
      addNotification({
        message: "Please fill in all required fields.",
        type: "error",
      });
      return;
    }

    const questionnaire_responses = Object.entries(formDataRef.current).map(([questionNumber, responseValue]) => ({
      question_number: parseInt(questionNumber),
      response_value: responseValue
    }));

    submitQuestionnaire(
      {
        token,
        questionnaire_responses
      },
      {
        onSuccess: (response) => {
          if (response.success) {
            setHasUnsavedChanges(false);
            setChangedQuestions(new Set()); // Clear all changed questions
          } else {
            addNotification({
              message: response.message || "Failed to submit questionnaire.",
              type: "error",
            });
          }
        },
        onError: (error) => {

          addNotification({
            message: "Failed to submit questionnaire. Please try again.",
            type: "error",
          });
        }
      }
    );
  }, [token, validateForm, submitQuestionnaire]);

  // Modal handlers
  const handleAutoFill = useCallback(() => {
    setIsAutoFillModalOpen(true);
  }, []);

  const handleRespondOffline = useCallback(() => {
    setIsRespondOfflineModalOpen(true);
  }, []);

  const handleAutoFillConfirm = useCallback(() => {
    setIsGenerating(true);
    // TODO: Implement actual auto-fill logic here
    setTimeout(() => {
      setIsGenerating(false);
      setIsAutoFillModalOpen(false);
      addNotification({
        message: "Auto-fill completed successfully!",
        type: "success",
      });
    }, 2000);
  }, []);

  const handleRespondOfflineConfirm = useCallback(async (response?: any) => {
    if (response?.success) {
      // Close modal first
      setIsRespondOfflineModalOpen(false);

      // Invalidate and refetch the questionnaire to get updated answers
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Questionnaire, questionnaireUrl]
      });

      // Wait a bit for the query to refetch
      await new Promise(resolve => setTimeout(resolve, 500));

      // Trigger form remount from parent to force complete rerender
      if (onFormRemount) {
        onFormRemount();
      }

      // Show success notification
      addNotification({
        message: "Offline responses submitted successfully!",
        type: "success",
      });
    } else {
      setIsRespondOfflineModalOpen(false);
    }
  }, [questionnaireUrl, queryClient, onFormRemount]);

  const handleDownloadQuestionnaire = useCallback(() => {
    setIsDownloading(true);
    // TODO: Implement questionnaire download logic here
    setTimeout(() => {
      setIsDownloading(false);
      addNotification({
        message: "Questionnaire downloaded successfully!",
        type: "success",
      });
    }, 1000);
  }, []);

  const handleFileUpload = useCallback((files: UploadedFile[]) => {
    setUploadedFiles(files);
  }, []);

  // Track previous filter values to detect actual changes
  const prevFilterRef = useRef<{ statusFilter: string; searchQuery: string } | null>(null);

  // Reset pagination when filters actually change
  useEffect(() => {
    if (prevFilterRef.current === null) {
      prevFilterRef.current = { statusFilter, searchQuery };
      return;
    }

    const filterChanged = 
      prevFilterRef.current.statusFilter !== statusFilter ||
      prevFilterRef.current.searchQuery !== searchQuery;
    
    if (filterChanged && currentPage !== 1) {
      resetPageNumber();
    }
    
    prevFilterRef.current = { statusFilter, searchQuery };
  }, [statusFilter, searchQuery, currentPage, resetPageNumber]);

  // Scroll to top of questions when page changes
  useEffect(() => {
    const questionsContainer = document.querySelector('[data-questions-scroll]');
    if (questionsContainer) {
      questionsContainer.scrollTop = 0;
    }
  }, [currentPage]);
  // Use a ref to avoid excessive re-renders when pagination component updates
  const prevPaginationRef = useRef<{
    currentPage: number;
    totalPages: number;
    rowsPerPage: number;
    isDisabled: boolean;
  } | null>(null);
  
  useEffect(() => {
    // Only update if relevant pagination data has changed
    const currentPaginationState = {
      currentPage,
      totalPages,
      rowsPerPage,
      isDisabled: isLoadingQuestions || isSubmitting
    };
    
    const hasPaginationChanged = !prevPaginationRef.current || 
      prevPaginationRef.current.currentPage !== currentPaginationState.currentPage ||
      prevPaginationRef.current.totalPages !== currentPaginationState.totalPages ||
      prevPaginationRef.current.rowsPerPage !== currentPaginationState.rowsPerPage ||
      prevPaginationRef.current.isDisabled !== currentPaginationState.isDisabled;
    
    if (onPaginationChange && totalPages > 0 && hasPaginationChanged) {
      prevPaginationRef.current = currentPaginationState;
      const paginationComponent = (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          isDisabled={isLoadingQuestions || isSubmitting}
        />
      );
      onPaginationChange(paginationComponent);
    } else if (onPaginationChange && (totalPages === 0 || !hasPaginationChanged)) {
      if (totalPages === 0) {
        prevPaginationRef.current = null;
        onPaginationChange(null);
      }
    }
  }, [currentPage, totalPages, rowsPerPage, handlePageChange, handleRowsPerPageChange, isLoadingQuestions, isSubmitting, onPaginationChange]);

  // Loading state
  if (isLoadingQuestions || (questionnaireUrl === undefined)) {
    return (
      <div className="flex items-center justify-center h-[calc(70vh)]">
        <div className="text-center">
          <Spinner />

        </div>
      </div>
    );
  }

  // Error state
  if (hasQuestionError || questionnaireUrl === null) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Unable to Load Questionnaire
          </h3>
          <p className="text-sm text-gray-600 mb-2">
            {questionError?.message || (questionnaireUrl === null ? 'No questionnaire URL provided. Please check with your administrator.' : 'Unknown error occurred.')}
          </p>
          {questionnaireUrl === null && (
            <p className="text-xs text-gray-500">
              Debug: questionnaireUrl is null
            </p>
          )}
        </div>
      </div>
    );
  }

  // No questions state
  if (questions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Questions Available
          </h3>
          <p className="text-sm text-gray-600">
            The questionnaire is empty or not yet configured.
          </p>
        </div>
      </div>
    );
  }



  return (
    <>
      {/* Questionnaire Header */}
      <QuestionnaireHeader
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onAutoFill={handleAutoFill}
        onRespondOffline={handleRespondOffline}
        isAutoFillModalOpen={isAutoFillModalOpen}
        setIsAutoFillModalOpen={setIsAutoFillModalOpen}
        isRespondOfflineModalOpen={isRespondOfflineModalOpen}
        setIsRespondOfflineModalOpen={setIsRespondOfflineModalOpen}
      />

    <div className="flex flex-col items-start p-0 gap-4 w-full max-w-[960px] mx-auto mt-4 px-6" data-questions-scroll>
      <div className="flex flex-col items-start gap-4 w-full">
        {paginatedQuestions.map((question) => (
          <div
            key={question.question_number}
            className="flex flex-col items-start p-0 gap-1.5 w-full"
          >
            <QuestionField
              question={question}
              value={formDataRef.current[question.question_number] || (question.question_type === 'multiple_choice' ? [] : '')}
              onChange={(value: string | string[]) => handleFieldChange(question.question_number, value)}
              error={errors[question.question_number]}
              onCommentChange={(comment: string) => handleCommentChange(question.question_number, comment)}
              {...(question.question_type === 'multiple_choice' && {
                enableImmediateApi: true,
                questionNumber: question.question_number,
                onImmediateChange: handleImmediateFieldChange
              })}
            />
          </div>
        ))}
          {filteredQuestions.length === 0 && (
            <div className="flex items-center justify-center h-[calc(60vh)]  w-full">
              <div className="text-center">
                <p className="text-foreground-400 mb-2">
                  No results found
                </p>
              </div>
            </div>
          )}
      </div>
    </div>

      {/* Auto-fill Modal */}
      <AutoFillWithAIModal
        isOpen={isAutoFillModalOpen}
        onClose={() => setIsAutoFillModalOpen(false)}
        onConfirm={handleAutoFillConfirm}
        onFileUpload={handleFileUpload}
        isGenerating={isGenerating}
      />

      {/* Respond Offline Modal */}
      <RespondOfflineModal
        isOpen={isRespondOfflineModalOpen}
        onClose={() => setIsRespondOfflineModalOpen(false)}
        onConfirm={handleRespondOfflineConfirm}
        onDownloadQuestionnaire={handleDownloadQuestionnaire}
        isDownloading={isDownloading}
        onFileUpload={handleFileUpload}
        token={token}
        questionnaireExcelUrl={questionnaireExcelUrl}
      />
    </>
  );
};

export default QuestionsForm;